import { getAppointmentDepositSummary } from "../../helpers/appointmentDetail";

// Deposit payment methods the appointment refund endpoint understands.
export const REFUND_PAYMENT_METHODS = [
  "Cash",
  "CreditCard",
  "BankTransfer",
  "StripeCreditCard",
  "Voucher",
  "Wallet",
];

export const refundPaymentMethodLabel = (method) => {
  if (method === "BankTransfer") return "Bank Transfer";
  if (method === "CreditCard") return "Credit Card";
  if (method === "StripeCreditCard") return "Stripe";
  return method;
};

export const refundPaymentMethodConfirmation = (method) => {
  if (method === "BankTransfer") return "via bank transfer";
  if (method === "CreditCard") return "via credit card";
  if (method === "Cash") return "via cash";
  if (method === "Voucher") return "via voucher";
  if (method === "Wallet") return "via wallet";
  return "via stripe";
};

// The method the deposit was actually paid with, when the refund endpoint
// supports refunding it. This is what makes a voucher-funded deposit refundable
// instead of only offering Cash/Credit Card/Stripe.
export const recordedRefundPaymentMethod = (detail) => {
  const { depositPaymentBy } = getAppointmentDepositSummary(detail);
  const normalized = String(depositPaymentBy || "").replace(/[\s_-]/g, "").toLowerCase();
  const method = {
    cash: "Cash",
    creditcard: "CreditCard",
    banktransfer: "BankTransfer",
    stripe: "StripeCreditCard",
    stripecreditcard: "StripeCreditCard",
    voucher: "Voucher",
    wallet: "Wallet",
  }[normalized] || "";
  return REFUND_PAYMENT_METHODS.includes(method) ? method : "";
};

const defaultRefundSource = detail => Array.isArray(detail?.refund_sources)
  ? detail.refund_sources.find(row => REFUND_PAYMENT_METHODS.includes(row.payment_method) && row.refundable_amount_minor > 0)?.payment_method || ''
  : recordedRefundPaymentMethod(detail);

// Keep the original tender separate from where the refund is delivered.
export const availableRefundPaymentMethods = (detail, selectedSource) => {
  const method = selectedSource || defaultRefundSource(detail);
  return !method ? [] : method === "Voucher" ? [method] : [
    ...(["StripeCreditCard", "BankTransfer"].includes(method) ? [method] : []), "Wallet", "Cash", "CreditCard",
  ];
};

export const refundRequestErrorMessage = (payload) => {
  const body = payload?.message ?? payload;
  return body?.error?.message
    || body?.message
    || payload?.userMessage
    || (typeof body === "string" ? body : "");
};

// Stripe refunds use the original server-held payment; no card entry is needed.
export const preselectedRefundPaymentMethod = (detail) => {
  return defaultRefundSource(detail);
};

export const isRefundPaymentMethodReady = (method) =>
  REFUND_PAYMENT_METHODS.includes(method);

export const buildAppointmentRefundValues = (detail, values) => {
  const source = values.refund_source || defaultRefundSource(detail);
  if (values.refund_source || Array.isArray(detail?.refund_sources)) {
    const original = originalRefundSources(detail).find(item => item.payment_method === source);
    if (!original) throw new Error("Choose a valid original payment method.");
    const limit = original.refundable_amount_minor ?? original.paid_amount_minor;
    if (Number(values.total_deposit_refund) * 100 > limit) throw new Error("Refund exceeds the available amount for this payment method.");
  }
  if (!availableRefundPaymentMethods(detail, source).includes(values.deposit_payment_by)) {
    throw new Error("Choose a supported refund method for this deposit.");
  }
  const refund = { ...values };
  delete refund["refund_source"];
  delete refund["stripe_token_id"];
  delete refund["stripe_payment_method_id"];
  delete refund["paymentIntent"];
  delete refund["error"];
  return { ...refund, deposit_payment_by: source, refund_to: values.deposit_payment_by };
};


// The appointment payload can carry several paid receipts; refund destinations
// on negative rows are never evidence of an original source tender.
export const originalRefundSources = detail => {
  if (Array.isArray(detail?.refund_sources)) return detail.refund_sources.filter(row => REFUND_PAYMENT_METHODS.includes(row.payment_method) && row.refundable_amount_minor > 0);
  const payments = Array.isArray(detail?.payment) ? detail.payment : detail?.payment ? [detail.payment] : [];
  const amounts = new Map();
  for (const row of payments) {
    if (String(row.status).toLowerCase() !== 'paid' || !(Number(row.amount) > 0)) continue;
    const kind = String(row.type || '').toLowerCase();
    if (!['deposit','sale','voucher','wallet','cash','creditcard','stripecreditcard','banktransfer'].includes(kind)) continue;
    const method = recordedRefundPaymentMethod({payment:[{...row,type:'Deposit',is_deposit:true,paidby:row.paidby || row.paid_by || row.type}]});
    if (method) amounts.set(method,(amounts.get(method)||0)+Math.round(Number(row.amount)*100));
  }
  return [...amounts].map(([payment_method,paid_amount_minor])=>({payment_method,paid_amount_minor,refundable_amount_minor:null}));
};
